import { type ReactNode, useId, useState } from 'react'
import { PexIcon } from '../../../components/icons/PexIcon'
import type { SellerStoresStrings } from '../../../i18n/seller/stores'
import { ProviderMark } from './ProviderMark'
import type { StoreProviderDefinition } from './store-providers'

export function ConnectedStoreGroup({
  provider,
  storeCount,
  attentionCount,
  copy,
  children,
}: {
  provider: StoreProviderDefinition
  storeCount: number
  attentionCount: number
  copy: SellerStoresStrings
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(true)
  const contentId = useId()

  return (
    <section
      role="region"
      aria-label={copy.group.regionLabel(provider.name)}
      className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-card)]"
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        aria-label={
          expanded ? copy.group.collapse(provider.name) : copy.group.expand(provider.name)
        }
        onClick={() => setExpanded((current) => !current)}
        className="flex min-h-[56px] w-full items-center gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-signal)] sm:px-4"
      >
        <ProviderMark provider={provider} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-[var(--color-text)]">
            {provider.name}
          </span>
          <span className="block text-xs text-[var(--color-text-muted)]">
            {copy.group.storeCount(storeCount)}
          </span>
        </span>
        <span
          className={`hidden rounded-full px-2.5 py-1 text-[11px] font-medium sm:inline-flex ${
            attentionCount > 0
              ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'
              : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
          }`}
        >
          {attentionCount > 0
            ? copy.group.needsAttention(attentionCount)
            : copy.group.allOperational}
        </span>
        <PexIcon
          name="caretDown"
          size="xs"
          tone="muted"
          className={`transition-transform motion-reduce:transition-none ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      {expanded ? (
        <div id={contentId} className="divide-y divide-[var(--color-border)] border-t border-[var(--color-border)]">
          {children}
        </div>
      ) : null}
    </section>
  )
}
